// US-first phone helpers — the app-wide display format is (nnn) nnn-nnnn,
// e.g. (208) 555-7000.

/**
 * Format a stored phone value for display. 10-digit US numbers (with or
 * without a leading 1 / +1, any punctuation) render in the standard
 * format; anything else (international, short codes) passes through
 * untouched.
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return ''
  const digits = value.replace(/\D/g, '')
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (ten.length !== 10) return value
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

/**
 * A typed phone number → E.164 (+1XXXXXXXXXX for US/CA), or null when it cannot be one.
 * The same rules as supabase/functions/_shared/phone.ts toE164, which is what the server
 * applies to the same input — a number this accepts, the server accepts.
 */
export function toE164(raw: string): string | null {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits[0] === '1') return `+${digits}`
  const intl = digits.startsWith('00') ? digits.slice(2) : digits
  if (intl.length >= 8 && intl.length <= 15 && !intl.startsWith('0')) return `+${intl}`
  return null
}

/**
 * Progressive input mask for controlled phone fields: formats while the
 * user types — (208 → (208) 555 → (208) 555-7000 — strips a leading 1/+1
 * and caps at 10 digits. Non-US values entered with a + prefix are left
 * alone so international numbers aren't mangled.
 */
export function maskPhoneInput(value: string): string {
  const trimmed = value.trim()
  if (trimmed.startsWith('+') && !/^\+1(\D|\d|$)/.test(trimmed)) return value
  let digits = value.replace(/\D/g, '')
  if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  if (digits.length === 0) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}
